import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { CancelTokenSource } from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import DatePicker from 'components/final-form/DatePicker';
import Input from 'components/final-form/Input';
import { StyledTable } from 'components/lab-central/TestKitReceptionView';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import BookingHistoryView from 'components/patient-entry/BookingHistoryView';
import PoolTestkitSingularization from 'components/patient-entry/bookings-table/PoolTestkitSingularization';
import TestResultActionButton from 'components/patient-entry/bookings-table/TestResultActionButton';
import SearchResultComponent from 'components/SearchResultComponent';
import { FormApi, FormState } from 'final-form';
import _ from 'lodash';
import moment from 'moment';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Dimmer, Grid, Icon, Loader, Message, Popup, SemanticCOLORS } from "semantic-ui-react";
import axios from 'services/http';
import { getInterventionBookingHistory } from 'services/interventionBookingServices';
import { getActiveInterventions } from 'services/interventionServices';
import { getTestInterventionBookings } from 'services/patientCentralServices';
import { debounce } from 'ts-debounce';
import { InterventionBookingTestResult, TestDateSearchBy, Urgency } from 'ts-types/api.enums';
import {
  InterventionBookingHistoryDto,
  InterventionDto,
  InterventionTestBookingSearchRequest,
  InterventionTestBookingSearchResult
} from 'ts-types/api.types';
import { ISO_DATE_FORMAT, isoToDisplayDate } from 'utils/dateUtils';
import { noop } from 'utils/functionUtils';

interface SingularizationFieldProps extends FormState {
  values: Partial<InterventionTestBookingSearchRequest>
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
}

interface State {
  testBookings: InterventionTestBookingSearchResult[],
  testBookingsLoading: boolean,
  searchValues: Partial<InterventionTestBookingSearchRequest>,
  interventionsMap: { [id: number]: InterventionDto },
  interventionBookingHistory: InterventionBookingHistoryDto[],
  interventionBookingHistoryLoading: boolean,
  poolSingularizationModalOpen: boolean;
  poolSingularizationId?: number;
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

class PoolTestkitSingularizationView extends Component<Props, State> {

  private mounted: boolean = false;

  initialValues: Partial<InterventionTestBookingSearchRequest> = {
    poolFilter: 'POOL_NOT_SINGULARIZED',
    includeAlreadySingularized: false,
    query: "",
    testKitCode: "",
    resultVerified: 'both',
    testDateSearchBy: 'BOOKING_DATE',
    testResult: 'POSITIVE',
    entryFromDate: moment().format(ISO_DATE_FORMAT),
    entryToDate: moment().format(ISO_DATE_FORMAT)
  }

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      testBookings: [],
      testBookingsLoading: false,
      searchValues: {
        ...this.initialValues
      },
      interventionsMap: {},
      interventionBookingHistory: [],
      interventionBookingHistoryLoading: false,
      poolSingularizationModalOpen: false,
      cancelTokenSource
    }

    this.fetchData(this.state.searchValues);
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  showPoolSingularizationModal = (poolTestId: number) => {
    this.setState(({}: State) => ({
      poolSingularizationModalOpen: true,
      poolSingularizationId: poolTestId
    }));
  };

  hidePoolSingularizationModal = () => {
    this.setState(({}: State) => ({
      poolSingularizationModalOpen: false
    }));
    this.fetchData(this.state.searchValues);
  };

  fetchData = (values: any) => {
    const {cancelTokenSource} = this.state;
    this.setState({testBookingsLoading: true});
    const {currentUser} = this.props;

    values.userMandateId = currentUser!.userConfig.mandateId;

    const onFinally = () => {
      this.setState({
        testBookingsLoading: false
      })
    };

    getActiveInterventions(cancelTokenSource)
        .then(response => {
          this.setState({
            interventionsMap: _.keyBy(response, "id")
          })
        })

    getTestInterventionBookings(values, cancelTokenSource)
        .then(response => {
              this.setState({
                testBookings: response,
                searchValues: values
              })
            }
        )
        .catch(noop)
        .finally(onFinally)
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  handleChange = debounce(({values}: SingularizationFieldProps, form: FormApi): void => {
    if (!this.mounted) {
      return;
    }

    if (values.includeAlreadySingularized) {
      values.poolFilter = 'POOL'
    } else {
      values.poolFilter = 'POOL_NOT_SINGULARIZED'
    }

    const fromDate = values.entryFromDate && new Date(values.entryFromDate);
    const toDate = values.entryToDate && new Date(values.entryToDate);

    this.setState({
          searchValues: values
        },
        () => {
          if (fromDate && toDate && fromDate.getTime() <= toDate.getTime()) {
            this.fetchData(values);
          }
        });

    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      form.change("entryToDate", moment(fromDate).format("YYYY-MM-DD"));
    }

  }, 300);

  render(): React.ReactNode {
    const {testBookingsLoading} = this.state;

    return (
        <PraxStyledSegment>
          <div>
            {this.renderSearchSegment()}
          </div>

          <StyledViewTableRow>
            {testBookingsLoading
                ? this.renderLoader()
                : this.renderTableSegment()
            }
          </StyledViewTableRow>
          {this.renderPoolTestSingularizationModal()}
        </PraxStyledSegment>
    );

  }

  renderLoader = (): React.ReactNode => {
    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">
            {getMessage("loading")}
          </Loader>
        </Dimmer>
    );
  };

  renderErrorSegment = (): React.ReactNode => {
    const {errorMessage} = this.state;

    return (
        <Grid.Row>
          <Grid.Column>
            <Message negative onDismiss={() => this.setErrorMessage()}>
              <p>{errorMessage}</p>
            </Message>
          </Grid.Column>
        </Grid.Row>
    );
  };

  renderSearchSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={this.initialValues}
            subscription={{pristine: true, values: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = ({values, form}: FormRenderProps): React.ReactNode => {
    const {getMessage} = this.props;
    const {errorMessage, testBookings, searchValues} = this.state;

    const disabledBefore = values.entryFromDate && new Date(values.entryFromDate);
    const totalResults = testBookings.length;

    return (
        <PxGrid>
          {errorMessage && this.renderErrorSegment()}
          <Grid.Row>
            <Grid.Column width={1}>
              {getMessage("from")}:
            </Grid.Column>
            <Grid.Column width={2}>
              <Field
                  name="entryFromDate"
                  component={DatePicker}
                  fluid
              />
            </Grid.Column>
            <Grid.Column width={2}>
              {getMessage("patientCentral.overview.tableHeader.testKitCode")}:
            </Grid.Column>
            <Grid.Column width={3}>
              <Field
                  name="testKitCode"
                  component={Input}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={1}>
              {getMessage("to")}:
            </Grid.Column>
            <Grid.Column width={2}>
              <Field
                  name="entryToDate"
                  component={DatePicker}
                  disabledBefore={disabledBefore}
                  fluid
              />
            </Grid.Column>
            <Grid.Column width={3}>
              <Field
                  name="includeAlreadySingularized"
                  component={CheckBox}
                  label={getMessage("labCentral.PoolTestkitSingularization.includeSingularizedPoolsLabel")}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={1}>
              <Button
                  type="button"
                  primary
                  size="tiny"
                  compact
                  onClick={() => this.fetchData(searchValues)}
              >
                {getMessage("search")}
              </Button>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <SearchResultComponent numResults={totalResults} textMessage={"search.resultsSummary"}/>
            </Grid.Column>
          </Grid.Row>
          <FormSpy subscription={{values: true}} onChange={formState => this.handleChange(formState, form)}/>
        </PxGrid>
    )
  }

  renderTableSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {testBookings} = this.state;

    let dateMessageKey = TestDateSearchBy.BOOKING_DATE;

    return (
        <StyledTable
            rowCount={testBookings.length}
            rowGetter={this.testBookingsRowGetter}
            rowRenderer={this.testBookingsRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 50,
                label: "#",
                dataKey: "index"
              },
              {
                width: 50,
                label: "",
                dataKey: "urgency",
                cellRenderer: this.urgencyCellRenderer
              },
              {
                width: 150,
                label: getMessage("patientCentral.overview.tableHeader.inputChannel"),
                dataKey: getMessage("inputChannel"),
                cellRenderer: this.inputChannelCellRenderer
              },
              {
                width: 200,
                label: getMessage(`enum.testDateSearchBy.${dateMessageKey}`),
                dataKey: "bookingDate",
                cellRenderer: this.entryDateCellRenderer
              },
              {
                width: 250,
                label: getMessage(`patientCentral.overview.tableHeader.intervention`),
                dataKey: "interventionId",
                cellRenderer: this.interventionCellRenderer
              },
              {
                width: 250,
                label: getMessage("patientCentral.overview.tableHeader.testKitCode"),
                dataKey: "testKitCode",
                cellRenderer: this.testKitCodeCellRenderer
              },
              {
                width: 150,
                label: getMessage("patientCentral.overview.tableHeader.testResult"),
                dataKey: "testResult",
                cellRenderer: this.testResultCellRenderer
              },
              {
                width: 150,
                label: getMessage("patientCentral.overview.tableHeader.rowActions"),
                dataKey: "firstName",
                cellRenderer: this.rowActionsCellRenderer
              },
              {
                width: 200,
                label: getMessage("patientCentral.overview.tableHeader.status"),
                dataKey: "status",
                cellRenderer: this.interventionStatusCellRenderer
              }
            ]}
        />
    )
  }

  testBookingsRowGetter = ({index}: any) => {
    const {testBookings} = this.state;

    Object.assign(testBookings[index], {index: index + 1});

    return testBookings[index];
  };

  testBookingsRowRenderer = ({className, columns, index, key, style}: any) => {

    const {testBookings} = this.state;
    const a11yProps = {"aria-rowindex": index + 1};

    if (testBookings[index].poolSingularized) {
      style.color = '#c6c6cb';
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
        >
          {columns}
        </div>
    );
  };

  urgencyCellRenderer = (data: any) => {
    const {getMessage} = this.props;
    const {cellData} = data;

    return cellData && cellData === Urgency.HIGH
        ? <Icon name="exclamation triangle"
                color="red"
                title={`${getMessage("patientCentral.checkIn.urgency")}: ${getMessage("enum.urgency.HIGH")}`}
        />
        : <></>;
  };

  inputChannelCellRenderer = (data: any) => {

    const {getMessage} = this.props;
    const {rowData} = data;

    const inputChannel = rowData.inputChannel;
    const inputChannelDisplay = getMessage(`enum.interventionBookingInputChannel.${inputChannel}`);

    return (
        <div>
          <div className="date birth-date" title={inputChannelDisplay}>{inputChannelDisplay}</div>
        </div>
    )
  };

  entryDateCellRenderer = (data: any) => {
    const {rowData} = data;

    const bookingDate = isoToDisplayDate(rowData.bookingDate);
    const bookingTime = rowData.bookingTime;
    const entryDateTimeTitle = `${bookingDate} - ${bookingTime}`;

    return (
        <div>
          <div className="date birth-date" title={entryDateTimeTitle}>
            {bookingDate} {bookingTime}</div>
        </div>
    )
  };

  interventionCellRenderer = (data: any) => {
    const {rowData} = data;
    const {interventionsMap} = this.state;
    const {language} = this.props;

    if (rowData.interventionId) {
      const intervention = interventionsMap[rowData.interventionId];
      if (intervention) {
        const defaultDesc = intervention.description
        const descMap: { [lang: string]: string } = {
          "de": defaultDesc,
          "en": intervention.descriptionEn || defaultDesc,
          "fr": intervention.descriptionFr || defaultDesc,
          "it": intervention.descriptionIt || defaultDesc
        };
        return descMap[language]
      }
    }

    return "";
  }

  testKitCodeCellRenderer = (data: any) => {
    const {cellData} = data;
    if (!cellData) {
      return "";
    }
    return cellData;
  };

  testResultCellRenderer = (data: any) => {

    const {getMessage} = this.props;
    const {rowData, cellData} = data;

    const icons: { [key: string]: { color: SemanticCOLORS } } = {
      [InterventionBookingTestResult.NEGATIVE]: {
        color: "green"
      },
      [InterventionBookingTestResult.POSITIVE]: {
        color: "red"
      },
      [InterventionBookingTestResult.UNCERTAIN]: {
        color: "grey"
      },
      [InterventionBookingTestResult.POOL_POSITIVE]: {
        color: "yellow"
      },
      "VACCINATED": {
        color: "blue"
      },
      "RECOVERED": {
        color: "blue"
      },
      "NEGATIVE_TEST_CERTIFICATE": {
        color: "blue"
      }
    }

    if (cellData && cellData.length > 0) {

      return <>
        <Icon
            title={getMessage(`enum.testResult.${cellData}`)}
            name={rowData.testResultVerified ? "check circle" : "circle outline"}
            color={icons[cellData].color}
            size="large"/>
      </>
    }

    return "";
  };

  onStatusColumnClick = (bookingId: number) => {
    const {cancelTokenSource} = this.state;

    this.setState({interventionBookingHistoryLoading: true});

    getInterventionBookingHistory(bookingId, cancelTokenSource)
        .then(response => {
          this.setState({
            interventionBookingHistory: response
          })
        })
        .finally(() => {
          this.setState({
            interventionBookingHistoryLoading: false
          })
        });
  };

  renderInterventionBookingHistory = (data: any) => {
    const {rowData} = data;
    const {getMessage} = this.props;
    const {interventionBookingHistory, interventionBookingHistoryLoading} = this.state;

    const trigger = (
        <div className="booking-history-icon-container">
          <div>
            <Icon
                className="booking-history-icon"
                name="history"
                onClick={() => this.onStatusColumnClick(rowData.bookingId)}/>
          </div>
        </div>
    )

    const loader = (
        <Loader active inline="centered" size="small">{getMessage("loading")}</Loader>
    );

    const content =
        interventionBookingHistoryLoading ? loader
            : <BookingHistoryView bookingHistory={interventionBookingHistory} currentStatus={rowData.status}/>

    return (
        <span>
      <Popup
          trigger={trigger}
          popperDependencies={[!!interventionBookingHistoryLoading]}
          popperModifiers={{
            preventOverflow: {
              boundary: document.getElementById("testKitReceptionMainContent")
            }
          }}
          content={content}
          on="click"
          size="small"
          position="left center"
      />
    </span>
    );
  }

  interventionStatusCellRenderer = (data: any) => {

    const {rowData} = data;
    const {getMessage} = this.props;

    const status = rowData.status;

    return <span>
      {getMessage(`enum.interventionBookingStatus.${status}`)}
      &nbsp;{this.renderInterventionBookingHistory(data)}
    </span>;
  };

  rowActionsCellRenderer = (data: any) => {
    const {getMessage} = this.props;
    const {rowData} = data;

    return (
        <TestResultActionButton
            hoverMessage={getMessage("patientCentral.overview.actions.singularize")}
            trigger={
              <Icon name={'sitemap'}
                    style={rowData.poolSingularized ? {color: "green"} : {}}
              />
            }
            onConfirm={() => {
              this.showPoolSingularizationModal(rowData.testId)
            }}
        />
    )
  }

  renderPoolTestSingularizationModal = () => {
    const {poolSingularizationId, poolSingularizationModalOpen} = this.state;

    if (!poolSingularizationModalOpen) {
      return <></>;
    }

    return <PoolTestkitSingularization poolTestId={poolSingularizationId}
                                       close={this.hidePoolSingularizationModal}
    />
  };

}

export default withMessages(withAuthContext(PoolTestkitSingularizationView));
